import { cycleInfo, cycleOn, hormonal } from "./cycle";
import { cycleGate } from "./cycleCare";
import { loggedStarts, periodLengthOf, regularity } from "./cycleView";
import { STRIP_WORDS, SYMPTOM_LABEL, forecastWords, stripsLabel, type PatternRow } from "./cycleWords";
import { addDays, dayMonth, daysBetween } from "./date";
import { SYMPTOM_ROWS, intensity, symptomsOf, type SymptomRowId } from "./symptoms";
import type { KayaState } from "./types";

export type { PatternRow } from "./cycleWords";

/**
 * Whether a symptom comes on the same days of the cycle, cycle after cycle (ADR-060).
 *
 * A pattern is named only from what she logged, only once it has held in three cycles, and only
 * for a window of a week or less: a run wider than that is most of the cycle, which
 * `cycleView.ts::cycleInsights` already says in its own sentence. A pattern changes nothing on
 * its own — the forecast is a notice to read, and when she then logs the symptom ADR-057's offers
 * apply. Everything here is read from the tombstoned `symptoms` set through `symptomsOf`; there is
 * no stored field.
 */

/** Days counted from the period's first day (day 1). */
export type Anchor = "start";

/** The last six logged starts at most — about six months, the diary's reach. */
export const MAX_CYCLES = 6;
/** A window wider than this is most of the cycle, not a pattern. */
export const MAX_WINDOW = 7;
/** Logged days further apart than this are separate runs: low energy on day 1 and days 26–28. */
const RUN_GAP = 7;
/** Cycles a pattern needs. */
export const PATTERN_CYCLES = 3;

export interface CycleDay {
  ds: string;
  /** cycle day, 1 = the period's first day */
  day: number;
  keys: string[];
}

export interface CycleRun {
  start: string;
  /** the day before the next logged start, or today for the current cycle */
  end: string;
  /** days from start to end */
  len: number;
  current: boolean;
  /** days 1..periodDays are the period's: the logged end or flow run, else the set length */
  periodDays: number;
  /** the days with anything logged, oldest first */
  days: CycleDay[];
}

export interface SymptomPattern {
  row: PatternRow;
  label: string;
  /** cycle days, inclusive — the window */
  from: number;
  to: number;
  /** cycles the symptom came in that window */
  cycles: number;
  /** cycles examined whose window has gone by */
  of: number;
  /** the window as dates in the current cycle; null with no current start */
  expected: { from: string; to: string } | null;
  state: "today" | "tomorrow" | "ahead" | "past";
  /** the usual intensity, 1..3: the median of the per-cycle maxima */
  typical: number;
  /** a period-day pattern whose window has passed: the same days from the predicted next start */
  next: { from: string; to: string } | null;
}

export interface NotYet {
  row: PatternRow;
  label: string;
  from: number;
  to: number;
  cycles: number;
  of: number;
}

export interface Forecast {
  when: "today" | "tomorrow";
  /** at most two */
  patterns: SymptomPattern[];
  /** patterns beyond the two named */
  more: number;
  /** the cycle day the sentence names: today's, or tomorrow's */
  day: number;
  text: string;
  link: string;
}

export const PATTERN_ROWS: PatternRow[] = SYMPTOM_ROWS.map((r) => r.id).filter(
  (id): id is PatternRow => id !== "flow"
);

/**
 * Whether a row counts as present on a day. Energy and sleep run the other way on the map
 * (3 is low or poor), so "okay" must not count: only low energy and poor sleep do.
 */
export function shows(keys: string[], row: SymptomRowId): boolean {
  const v = intensity(keys, row);
  return v !== null && v >= (row === "energy" || row === "sleep" ? 2 : 1);
}

/** Each logged cycle, oldest first, with the days she logged anything on ("fine" included). */
export function cycleHistory(S: KayaState, today: string): CycleRun[] {
  const all = loggedStarts(S, today);
  const from = Math.max(0, all.length - MAX_CYCLES);
  const out: CycleRun[] = [];
  for (let i = from; i < all.length; i++) {
    const start = all[i];
    const next = all[i + 1];
    const end = next ? addDays(next, -1) : today;
    const days: CycleDay[] = [];
    for (let ds = start, d = 1; ds <= end; ds = addDays(ds, 1), d++) {
      const keys = symptomsOf(S, ds);
      if (keys.length) days.push({ ds, day: d, keys });
    }
    out.push({
      start,
      end,
      len: daysBetween(start, end) + 1,
      current: !next,
      periodDays: periodLengthOf(S, start, next) ?? (S.cycle.periodLen || 5),
      days,
    });
  }
  return out;
}

type Run = [number, number];

/** A cycle's days with the row, split where they lie more than a week apart. */
function runsOf(c: CycleRun, row: PatternRow): Run[] {
  const runs: Run[] = [];
  for (const d of c.days) {
    if (!shows(d.keys, row)) continue;
    const last = runs[runs.length - 1];
    if (last && d.day - last[1] <= RUN_GAP) last[1] = d.day;
    else runs.push([d.day, d.day]);
  }
  return runs;
}

/**
 * The window's ends. With an even count the median falls between two days, and the window takes
 * the wider of them at each end — the earlier first day, the later last day — so a symptom on
 * days 26 and 27 in two cycles reads "days 26–27", not a single day neither cycle matched.
 */
function windowOf(runs: Run[]): Run {
  const firsts = runs.map((r) => r[0]).sort((a, b) => a - b);
  const lasts = runs.map((r) => r[1]).sort((a, b) => a - b);
  const mid = (xs: number[]) => (xs[Math.floor((xs.length - 1) / 2)] + xs[Math.ceil((xs.length - 1) / 2)]) / 2;
  return [Math.floor(mid(firsts)), Math.ceil(mid(lasts))];
}

const median = (xs: number[]) => {
  const s = xs.slice().sort((a, b) => a - b);
  return (s[Math.floor((s.length - 1) / 2)] + s[Math.ceil((s.length - 1) / 2)]) / 2;
};

interface Candidate {
  from: number;
  to: number;
  cycles: number;
  of: number;
  typical: number;
}

/** Every window a row's runs suggest, with how many cycles fit it. */
function candidates(cycles: CycleRun[], row: PatternRow, today: string): Candidate[] {
  const runs = cycles.map((c) => runsOf(c, row));
  const seen = new Set<string>();
  const out: Candidate[] = [];
  for (const seeds of runs)
    for (const seed of seeds) {
      const near = (r: Run) => r[1] >= seed[0] - 1 && r[0] <= seed[1] + 1;
      const matched = runs.map((rs) => rs.find(near)).filter((r): r is Run => !!r);
      const [from, to] = windowOf(matched);
      const id = `${from}-${to}`;
      if (seen.has(id)) continue;
      seen.add(id);
      let of = 0;
      const maxima: number[] = [];
      cycles.forEach((c) => {
        if (!c.days.length) return;
        // the window, a day either side, has wholly gone by — and, for a closed cycle, existed
        const past = addDays(c.start, to) < today && (c.current || to <= c.len);
        if (!past) return;
        of++;
        const inside = c.days.filter((d) => d.day >= from - 1 && d.day <= to + 1 && shows(d.keys, row));
        if (inside.length)
          maxima.push(Math.max(...inside.map((d) => intensity(d.keys, row) ?? 0)));
      });
      out.push({
        from,
        to,
        cycles: maxima.length,
        of,
        typical: maxima.length ? Math.max(1, Math.min(3, Math.round(median(maxima)))) : 1,
      });
    }
  return out;
}

const rank = (a: Candidate, b: Candidate) => b.cycles / (b.of || 1) - a.cycles / (a.of || 1) || a.from - b.from;

function stateOf(ds: string, w: { from: string; to: string }): SymptomPattern["state"] {
  if (ds >= w.from && ds <= w.to) return "today";
  if (ds > w.to) return "past";
  return addDays(ds, 1) === w.from ? "tomorrow" : "ahead";
}

/**
 * The patterns that hold, and the rows waiting on one more cycle. One pattern a row at most —
 * the window most cycles fit — so low energy on the first day of some periods does not hide the
 * days before the next one where it comes every time.
 */
export function patternsFor(S: KayaState, today: string): { patterns: SymptomPattern[]; notYet: NotYet[] } {
  if (!cycleOn(S)) return { patterns: [], notYet: [] };
  const cycles = cycleHistory(S, today);
  if (!cycles.length) return { patterns: [], notYet: [] };
  const cur = cycles[cycles.length - 1];
  const nextStart = cycleInfo(S, today)?.next ?? null;
  const patterns: SymptomPattern[] = [];
  const notYet: NotYet[] = [];

  for (const row of PATTERN_ROWS) {
    const all = candidates(cycles, row, today).filter((c) => c.to - c.from + 1 <= MAX_WINDOW);
    const holds = all.filter((c) => c.cycles >= PATTERN_CYCLES).sort(rank)[0];
    const label = SYMPTOM_LABEL[row];
    if (holds) {
      const expected = { from: addDays(cur.start, holds.from - 1), to: addDays(cur.start, holds.to - 1) };
      const state = stateOf(today, expected);
      const periodDays = holds.from <= (S.cycle.periodLen || 5);
      patterns.push({
        row,
        label,
        from: holds.from,
        to: holds.to,
        cycles: holds.cycles,
        of: holds.of,
        expected,
        state,
        typical: holds.typical,
        next:
          state === "past" && periodDays && nextStart
            ? { from: addDays(nextStart, holds.from - 1), to: addDays(nextStart, holds.to - 1) }
            : null,
      });
      continue;
    }
    // "after one more cycle": a row that has not yet come in two cycles stays silent
    const wait = all.filter((c) => c.cycles === PATTERN_CYCLES - 1).sort(rank)[0];
    if (wait) notYet.push({ row, label, from: wait.from, to: wait.to, cycles: wait.cycles, of: wait.of });
  }

  patterns.sort((a, b) => b.cycles / b.of - a.cycles / a.of || a.from - b.from);
  notYet.sort((a, b) => a.from - b.from || a.label.localeCompare(b.label));
  return { patterns, notYet };
}

/**
 * Today's notice: the patterns whose window she is in, else the ones that start tomorrow. Never
 * one inside the period's own days — the period notice and ADR-057's offers speak for those — and
 * never one she has already logged today, when ADR-057's words apply instead.
 */
export function forecastFor(S: KayaState, ds: string): Forecast | null {
  if (!cycleOn(S)) return null;
  const gate = cycleGate(S, ds);
  if (gate === "off" || gate === "pregnant") return null;
  const last = cycleInfo(S, ds)?.last;
  if (!last) return null;
  const day = daysBetween(last, ds) + 1;
  const keys = symptomsOf(S, ds);
  const periodLen = S.cycle.periodLen || 5;
  const { patterns } = patternsFor(S, ds);
  const ok = (p: SymptomPattern) => p.from > periodLen && !shows(keys, p.row);

  let when: Forecast["when"] = "today";
  let list = patterns.filter((p) => p.state === "today" && ok(p));
  if (!list.length) {
    when = "tomorrow";
    list = patterns.filter((p) => p.state === "tomorrow" && ok(p));
  }
  if (!list.length) return null;
  const shown = list.slice(0, 2);
  const more = list.length - shown.length;
  const named = when === "today" ? day : day + 1;
  return {
    when,
    patterns: shown,
    more,
    day: named,
    text: forecastWords({ when, day: named, patterns: shown, more }),
    link: `/progress/cycle/diary/${shown[0].row}`,
  };
}

/* ---------- the diary ---------- */

export interface SymptomTally {
  row: PatternRow;
  label: string;
  /** days logged */
  days: number;
  /** cycles with the row at all */
  cycles: number;
  /** cycles with anything logged */
  of: number;
  pattern: SymptomPattern | null;
  notYet: NotYet | null;
}

/** Every row ever logged in the diary's cycles, most days first. */
export function symptomTallies(S: KayaState, today: string): SymptomTally[] {
  const cycles = cycleHistory(S, today);
  const examined = cycles.filter((c) => c.days.length).length;
  const { patterns, notYet } = patternsFor(S, today);
  const out: SymptomTally[] = [];
  for (const row of PATTERN_ROWS) {
    let days = 0;
    let n = 0;
    for (const c of cycles) {
      const k = c.days.filter((d) => shows(d.keys, row)).length;
      days += k;
      if (k) n++;
    }
    if (!days) continue;
    out.push({
      row,
      label: SYMPTOM_LABEL[row],
      days,
      cycles: n,
      of: examined,
      pattern: patterns.find((p) => p.row === row) ?? null,
      notYet: notYet.find((p) => p.row === row) ?? null,
    });
  }
  return out.sort((a, b) => b.days - a.days || a.label.localeCompare(b.label));
}

/** The diary's count: days logged, the cycles they fall in, and the first of them. */
export function diaryCount(S: KayaState, today: string): { days: number; cycles: number; since: string | null } {
  const cycles = cycleHistory(S, today).filter((c) => c.days.length);
  return {
    days: cycles.reduce((a, c) => a + c.days.length, 0),
    cycles: cycles.length,
    since: cycles.length ? cycles[0].days[0].ds : null,
  };
}

/* ---------- the strips (kit §54) ---------- */

export interface StripRow {
  start: string;
  /** "16 Jul", "11 Oct · now" */
  label: string;
  current: boolean;
  /** one per cycle day drawn; days after today in the current cycle are not drawn */
  cells: (0 | 1 | 2 | 3)[];
  periodDays: number;
}

export interface Strips {
  /** columns: the longest cycle drawn, at least the average */
  len: number;
  ovulationDay: number | null;
  /** the current cycle's day today, for the rule above its row */
  todayDay: number | null;
  /** oldest at the top */
  rows: StripRow[];
  window: { from: number; to: number } | null;
  ariaLabel: string;
}

/** One symptom by cycle day, a row per cycle — what the symptom page draws. */
export function stripsFor(S: KayaState, today: string, row: PatternRow): Strips | null {
  const cycles = cycleHistory(S, today);
  if (!cycles.length) return null;
  const { patterns, notYet } = patternsFor(S, today);
  const p = patterns.find((x) => x.row === row) ?? null;
  const rows: StripRow[] = cycles.map((c) => {
    const byDay = new Map(c.days.map((d) => [d.day, d.keys]));
    const cells: (0 | 1 | 2 | 3)[] = [];
    for (let d = 1; d <= c.len; d++) {
      const keys = byDay.get(d);
      const v = keys ? intensity(keys, row) ?? 0 : 0;
      cells.push(Math.max(0, Math.min(3, Math.round(v))) as 0 | 1 | 2 | 3);
    }
    return {
      start: c.start,
      label: c.current ? `${dayMonth(c.start)}${STRIP_WORDS.now}` : dayMonth(c.start),
      current: c.current,
      cells,
      periodDays: c.periodDays,
    };
  });
  const avg = regularity(S, today).avgCycle;
  const len = Math.max(avg, ...cycles.map((c) => c.len));
  const cur = cycles[cycles.length - 1];
  const days = cycles.reduce((a, c) => a + c.days.filter((d) => shows(d.keys, row)).length, 0);
  return {
    len,
    ovulationDay: hormonal(S) ? null : avg - 14,
    todayDay: cur.len,
    rows,
    window: p ? { from: p.from, to: p.to } : null,
    ariaLabel: stripsLabel({
      label: SYMPTOM_LABEL[row],
      cycles: cycles.length,
      pattern: p,
      days,
      notYet: notYet.find((x) => x.row === row) ?? null,
    }),
  };
}
